import React, { useEffect } from 'react';
import {
    setXIdSociete,
    useSociete,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
    utilisateursSelectorMain,
} from '@europrocurement/l2d-domain';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router';
import {
    CenterCircularProgress,
    useAvatarUrl,
    useAvatarUrlDispatch,
} from '@europrocurement/flexy-components';
import { sha256 } from '@europrocurement/l2d-utils';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';

/**
 * UtilisateurRouter wrapper for setting default society L2D.
 */
export const UtilisateursOutlet: React.FunctionComponent = function () {
    const dispatch = useDispatch();
    const xIdSociete = useSociete();

    const dispatchUtilisateurs = useDispatch<UtilisateursAppDispatch>();
    const avatarUrlDispatch = useAvatarUrlDispatch();
    const { avatarBaseUrl } = useUtilisateurService();
    const keycloak = UseKeycloakService();
    const [keycloakIdHash, setKeycloakIdHash] = React.useState<string | undefined>(undefined);
    const { avatarUrl, usersAvatarInfos } = useAvatarUrl();

    useEffect(() => {
        if (xIdSociete !== 12) {
            dispatch(setXIdSociete(12));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { getServices, getPoles, getMarques, getGroupes, getAntennes, getUtilisateurs } =
        useUtilisateurService();

    const utilisateurs = useUtilisateursServiceSelector(utilisateursSelectorMain).data;

    useEffect(() => {
        dispatchUtilisateurs(getServices({}));
        dispatchUtilisateurs(getPoles({}));
        dispatchUtilisateurs(getMarques({}));
        dispatchUtilisateurs(getGroupes({}));
        dispatchUtilisateurs(getAntennes({}));
        dispatchUtilisateurs(getUtilisateurs({}));
        sha256(keycloak.getTokenParsed().preferred_username).then(setKeycloakIdHash);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // on mount only

    // Initialise la liste des utilisateurs et leur url avatar pour le context des avatars avant le rendu de la liste des utilisateurs
    useEffect(() => {
        // Cherche si les informations de cet utilisateur sont déjà dans le context avatar
        utilisateurs.forEach((user) => {
            if (user.userIdentifier !== undefined && user.userIdentifier !== null) {
                if (usersAvatarInfos !== undefined) {
                    const inContext = usersAvatarInfos.find(
                        (element) => element.avatarUserIdentifier === user.userIdentifier,
                    );

                    // Si le résultat n'apparaît pas dans le find, il faut ajouter les informations de l'utilisateur au context
                    if (!inContext) {
                        // hash l'identifiant, formate les données, puis dispatch dans le context
                        sha256(user.userIdentifier).then((hash) => {
                            // reprend les données existantes et ajoute la nouvelle
                            avatarUrlDispatch({
                                type: 'update',
                                payload: avatarUrl,
                                list: [
                                    ...usersAvatarInfos,
                                    {
                                        avatarUserIdentifier: user.userIdentifier as string,
                                        currentUserAvatarUrl: `${avatarBaseUrl}${hash}?time=${new Date().getTime()}`,
                                    },
                                ],
                            });
                        });
                    }
                } else {
                    // hash l'identifiant, formate les données, puis dispatch dans le context
                    sha256(user.userIdentifier).then((hash) => {
                        // reprend les données existantes et ajoute la nouvelle
                        avatarUrlDispatch({
                            type: 'update',
                            payload: avatarUrl,
                            list: [
                                {
                                    avatarUserIdentifier: user.userIdentifier as string,
                                    currentUserAvatarUrl: `${avatarBaseUrl}${hash}?time=${new Date().getTime()}`,
                                },
                            ],
                        });
                    });
                }
            }
        });
    }, [
        avatarBaseUrl,
        avatarUrl,
        avatarUrlDispatch,
        keycloakIdHash,
        usersAvatarInfos,
        utilisateurs,
    ]);

    if (xIdSociete === 12) {
        return <Outlet />;
    }
    return <CenterCircularProgress />;
};
